import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PerimetersService } from '../../../@core/utils/perimeters.service';
import { ScopeService } from '../../../@core/utils/scope.service';
import { PerimeterEnergyTrajectoryService } from '../../../@core/utils/perimeter-energy-trajectory.service';
import { ParentPerimeterTypeFilter } from '../../../@core/parent-perimeter-type-filter';
import { KPI } from '../../../@core/api';
import { NbSelectModule, NbOptionModule } from '@nebular/theme';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';

export interface BuildingOption {
  id: number;
  buildingId: number;
  name: string;
  flooringArea: number;
  perimeterType: number;
  disabled: boolean;
}

@Component({
  selector: 'app-building-selector',
  templateUrl: './building-selector.component.html',
  styleUrls: ['./building-selector.component.scss'],
  standalone: true,
  imports: [NbSelectModule, NbOptionModule, FormsModule, TranslateModule, NgClass],
})
export class BuildingSelectorComponent implements OnInit, OnDestroy {

  @Output() buildingSelectionChanged = new EventEmitter<number[]>();

  public buildings: BuildingOption[] = [];
  public selectedBuildingIds: number[] = [];
  public visible = false;
  public isAllSelected = false;
  public readonly ALL_OPTION_VALUE = '__ALL__';
  public hasDisabledBuildings = false;
  public displaySelection: (number | string)[] = [];
  private previousDisplaySelection: (number | string)[] = [];

  public get showAllOption(): boolean {
    return this.buildings.length > 1;
  }

  private subscriptions: Subscription[] = [];
  private static readonly STORAGE_KEY = 'building-selector-selection';
  private currentSiteId: number;
  private childBuildingIds: { [buildingId: number]: number[] } = {};

  constructor(
    private perimetersService: PerimetersService,
    private scopeService: ScopeService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
  ) {}

  private saveToStorage(): void {
    if (this.currentSiteId == null) return;
    const key = `${BuildingSelectorComponent.STORAGE_KEY}_${this.currentSiteId}`;
    localStorage.setItem(key, JSON.stringify(this.selectedBuildingIds));
  }

  private loadFromStorage(siteId: number): number[] | null {
    const key = `${BuildingSelectorComponent.STORAGE_KEY}_${siteId}`;
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    try {
      const ids = JSON.parse(stored);
      return Array.isArray(ids) ? ids : null;
    } catch {
      return null;
    }
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([
        this.perimetersService.perimeters$,
        this.perimetersService.activePerimeterFilters$,
        this.scopeService.perimeterTypes$,
      ]).pipe(
        switchMap(([perimeters, activeFilters, perimeterTypes]) => {
          // Only show when a single site is selected
          if (perimeters.length !== 1) {
            return [{ visible: false, buildings: [], siteId: null, childBuildingIds: {} }];
          }

          const site = perimeters[0];
          const subPerimeters = (site.sub_perimeters || []).filter(sp =>
            sp.level_parent === null &&
            perimeterTypes[sp.perimeter_type]?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING
          );

          // Get the perimeter type filter if active
          const perimeterTypeFilter = activeFilters.find(
            f => f.id === 'parent-perimeter-type'
          ) as ParentPerimeterTypeFilter | undefined;

          return this.perimeterEnergyTrajectoryService.getPerimeterEnergyTrajectories(site.id).pipe(
            map((trajectories) => {
              const perimeterIdsWithTrajectoryRecords = new Set(
                trajectories
                  .filter(t => t.trajectory_records?.some(r => r.consumption > 0))
                  .map(t => t.perimeter)
              );

              let buildings: BuildingOption[] = subPerimeters.map(sp => ({
                id: sp.id,
                buildingId: sp.building_id,
                name: sp.name,
                flooringArea: sp.flooring_area,
                perimeterType: sp.perimeter_type,
                disabled: !sp.building_id || !sp.flooring_area || !perimeterIdsWithTrajectoryRecords.has(sp.id),
              }));

              if (perimeterTypeFilter?.selectedValues?.length) {
                const allowedTypes = perimeterTypeFilter.selectedValues.map(Number);
                buildings = buildings.filter(b => allowedTypes.includes(b.perimeterType));
              }

              // Child building ids per level-one building, walking the level_parent tree
              const allSubPerimeters = site.sub_perimeters || [];
              const childBuildingIds: { [buildingId: number]: number[] } = {};
              subPerimeters.forEach(sp => {
                if (!sp.building_id) return;
                const ids: number[] = [];
                const stack = [sp.id];
                while (stack.length) {
                  const parentId = stack.pop();
                  allSubPerimeters.forEach((child) => {
                    if (child.level_parent === parentId) {
                      if (child.building_id) ids.push(child.building_id);
                      stack.push(child.id);
                    }
                  });
                }
                childBuildingIds[sp.building_id] = ids;
              });

              return { visible: true, buildings, siteId: site.id, childBuildingIds };
            }),
          );
        }),
      ).subscribe(({ visible, buildings, siteId, childBuildingIds }) => {
        this.visible = visible;
        this.currentSiteId = siteId;
        this.childBuildingIds = childBuildingIds || {};

        const availableBuildingIds = buildings
          .filter(b => !b.disabled)
          .map(b => b.buildingId);

        this.buildings = buildings;

        // Restore selection from localStorage, filtering to still-valid IDs
        const stored = this.loadFromStorage(siteId);
        if (buildings.length === 1) {
          // Only one building to choose from: always select it, even if disabled (e.g. missing data)
          this.selectedBuildingIds = [buildings[0].buildingId];
        } else if (stored) {
          const validStored = stored.filter(id => availableBuildingIds.includes(id));
          this.selectedBuildingIds = validStored.length > 0 ? validStored : availableBuildingIds;
        } else {
          this.selectedBuildingIds = availableBuildingIds;
        }

        this.hasDisabledBuildings = buildings.some(b => b.disabled);
        this.isAllSelected = !this.hasDisabledBuildings
          && availableBuildingIds.length > 0
          && this.selectedBuildingIds.length === availableBuildingIds.length
          && availableBuildingIds.every(id => this.selectedBuildingIds.includes(id));
        this.updateDisplaySelection();
        this.buildingSelectionChanged.emit(this.getEffectiveBuildingIds());
        const selectedPerimeterIds = this.buildings
          .filter(b => this.selectedBuildingIds.includes(b.buildingId))
          .map(b => b.id);
        this.perimeterEnergyTrajectoryService.setTrajectorySelection(this.selectedBuildingIds, selectedPerimeterIds);
      }),
    );
  }

  private getEffectiveBuildingIds(): number[] {
    return this.selectedBuildingIds.reduce<number[]>(
      (ids, id) => ids.concat(id, this.childBuildingIds[id] || []),
      [],
    );
  }

  public onDisplaySelectionChange(selected: (number | string)[]): void {
    const hadAll = this.previousDisplaySelection.includes(this.ALL_OPTION_VALUE);
    const hasAll = selected.includes(this.ALL_OPTION_VALUE);
    const toggledAll = hadAll !== hasAll;

    const availableBuildingIds = this.buildings
      .filter(b => !b.disabled)
      .map(b => b.buildingId);

    let newSelectedIds: number[];

    if (toggledAll) {
      if (this.isAllSelected) {
        // Unchecking "All" → keep current selection (at least one must remain)
        newSelectedIds = [...this.selectedBuildingIds];
      } else {
        // Checking "All" → select everything
        newSelectedIds = availableBuildingIds;
      }
    } else {
      // Individual item toggled
      newSelectedIds = selected.filter(
        (v): v is number => typeof v === 'number'
      );
    }

    // Prevent deselecting all — at least one must remain
    if (newSelectedIds.length === 0) {
      this.updateDisplaySelection();
      return;
    }

    this.selectedBuildingIds = newSelectedIds;
    this.hasDisabledBuildings = this.buildings.some(b => b.disabled);
    this.isAllSelected =
      !this.hasDisabledBuildings &&
      this.selectedBuildingIds.length === availableBuildingIds.length &&
      availableBuildingIds.every(id => this.selectedBuildingIds.includes(id));

    this.updateDisplaySelection();
    this.saveToStorage();
    this.buildingSelectionChanged.emit(this.getEffectiveBuildingIds());
    const selectedPerimeterIds = this.buildings
      .filter(b => this.selectedBuildingIds.includes(b.buildingId))
      .map(b => b.id);
    this.perimeterEnergyTrajectoryService.setTrajectorySelection(this.selectedBuildingIds, selectedPerimeterIds);
  }

  private updateDisplaySelection(): void {
    this.displaySelection = (this.showAllOption && this.isAllSelected)
      ? [this.ALL_OPTION_VALUE, ...this.selectedBuildingIds]
      : [...this.selectedBuildingIds];
    this.previousDisplaySelection = [...this.displaySelection];
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
